import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import type { Locale, SectionKey } from "../../shared/dto";
import { PartnerControl } from "../PartnerControl";
import { transcribeLive } from "../VoiceableInput";
import { confirmForClient, formatDate, makeSafeT, orderedSections, phaseLabel } from "./core";
import { parseValue, type StaffPaneProps } from "./console-types";
import "./crm-card.css";
export function CrmCard(props: { clientId: string; t: (key: string) => string; locale: Locale }) {
  const [state, setState] = useState<"loading" | "off" | "none" | "ready" | "error">("loading");
  const [lead, setLead] = useState<Record<string, unknown> | null>(null);
  const load = useCallback(async () => {
    setState("loading");
    setLead(null);
    try {
      const response = await fetch(`/api/staff/clients/${props.clientId}/crm`, { credentials: "include" });
      const body = await response.json() as { ok: boolean; available?: boolean; failed?: boolean; lead?: Record<string, unknown> | null };
      if (!response.ok || !body.ok) throw new Error("crm_failed");
      if (!body.available) { setState("off"); return; }
      if (body.failed) { setState("error"); return; }
      if (!body.lead) { setState("none"); return; }
      setLead(body.lead); setState("ready");
    } catch { setState("error"); }
  }, [props.clientId]);
  useEffect(() => { void load(); }, [load]);
  if (state === "off") return null;
  /**
   * 🔴 F138 kolečko 3: a refused CRM lookup is a QUIET ROW, not a card.
   *
   * The card was honest and it was permanent: dev has no CRM, so on every
   * client, every day, a full framed block with a heading, a warning mark and
   * two sentences stood in the overview saying that an optional integration is
   * unavailable and that nothing about the client has changed. Weight is a
   * claim about importance, and this failure costs the partner nothing. It
   * keeps its own sentence and its way out; it stops taking a card's worth of
   * the page (verdict fault 8).
   */
  if (state === "error") {
    return (
      <p className="staff-crm-quiet" role="status" data-crm-state="error">
        <span className="staff-crm-quiet-mark" aria-hidden="true">!</span>
        <span>
          {props.t("staffConsole.crm.error")}{" "}
          <span className="staff-crm-quiet-hint">{props.t("staffConsole.crm.errorHint")}</span>
        </span>
        <button className="text-button" onClick={() => void load()} type="button">
          {props.t("common.retry")}
        </button>
      </p>
    );
  }
  return (
    <article className={`staff-card staff-crm-card state-${state}`} aria-live="polite">
      <h3>{props.t("staffConsole.crm.title")}</h3>
      {state === "loading" && <p className="staff-crm-status">{props.t("common.loading")}</p>}
      {state === "none" && <p className="staff-empty">{props.t("staffConsole.crm.none")}</p>}
      {state === "ready" && lead && (
        <dl className="staff-definition">
          {typeof lead.company === "string" && lead.company && <div><dt>{props.t("staffConsole.crm.company")}</dt><dd>{lead.company}</dd></div>}
          {typeof lead.phone === "string" && lead.phone && <div><dt>{props.t("staffConsole.crm.phone")}</dt><dd>{lead.phone}</dd></div>}
          {typeof lead.website === "string" && lead.website && <div><dt>{props.t("staffConsole.crm.website")}</dt><dd><a className="quiet-link" href={String(lead.website)} target="_blank" rel="noreferrer">{lead.website}</a></dd></div>}
          <div><dt>{props.t("staffConsole.crm.meeting")}</dt><dd>{lead.hasMeeting
            ? `${props.t("staffConsole.crm.meetingYes")}${typeof lead.nextMeetingDate === "string" && lead.nextMeetingDate ? ` · ${formatDate(String(lead.nextMeetingDate), props.locale)}` : ""}`
            : props.t("staffConsole.crm.meetingNo")}</dd></div>
        </dl>
      )}
    </article>
  );
}

// Proactive e-mail to the client, composed by the partner (Pavel 2026-07-21).
export function NotifyClientCard(props: StaffPaneProps) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  async function send(event: FormEvent) {
    event.preventDefault();
    setState("sending");
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/notify`, {
      method: "POST", body: { subject, message }
    }, { refresh: false });
    if (response.ok) { setState("sent"); setSubject(""); setMessage(""); }
    else setState("error");
  }
  return (
    <article className="staff-card notify-card">
      <h3>{props.t("staffConsole.notify.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.notify.hint")}</p>
      <form className="editor-form" onSubmit={send}>
        <input type="text" value={subject} maxLength={120} placeholder={props.t("staffConsole.notify.subject")}
          onChange={(event) => { setSubject(event.target.value); setState("idle"); }} required minLength={3} />
        <textarea value={message} maxLength={2000} rows={4} placeholder={props.t("staffConsole.notify.message")}
          onChange={(event) => { setMessage(event.target.value); setState("idle"); }} required minLength={3} />
        <div className="button-row">
          <button className="primary-button" type="submit" disabled={state === "sending" || !subject.trim() || !message.trim()}>
            {props.t(state === "sending" ? "staffConsole.notify.sending" : "staffConsole.notify.send")}
          </button>
          {state === "sent" && <span className="message success-message">{props.t("staffConsole.notify.sent")}</span>}
          {state === "error" && <span className="message error-message">{props.t("common.error")}</span>}
        </div>
      </form>
    </article>
  );
}


// ---- Partner acts for the client (meeting 24.7. D3 + D18) ----
// Materials the partner supplies ON BEHALF of the client: recordings, scans
// (PDF), photos and links, each with an optional note. The client sees the
// list in the know-how section attributed "nahráno parťákem"; the Mini
// training payload carries the same catalog.
export function MaterialsCard(props: StaffPaneProps) {
  const [scope, setScope] = useState<"knowhow" | "legacy">("knowhow");
  const [note, setNote] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "error">("idle");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const catalogRow = props.detail.resources.find((row) => String(row.key) === "partner_materials");
  const catalog = catalogRow ? parseValue(catalogRow) : {};
  const items = Array.isArray(catalog.items) ? catalog.items as Array<Record<string, unknown>> : [];

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setState("busy");
    const form = new FormData();
    form.set("file", file);
    form.set("scope", scope);
    if (note.trim()) form.set("note", note.trim());
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/materials`, { method: "POST", body: form });
    if (response.ok) { setNote(""); setState("idle"); } else setState("error");
  }

  async function addLink(event: FormEvent) {
    event.preventDefault();
    if (!linkUrl.trim()) return;
    setState("busy");
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/materials/link`, {
      method: "POST",
      body: { url: linkUrl.trim(), label: linkLabel.trim() || undefined, note: note.trim() || undefined, scope }
    });
    if (response.ok) { setLinkUrl(""); setLinkLabel(""); setNote(""); setState("idle"); } else setState("error");
  }

  async function remove(itemId: string) {
    if (!confirmForClient(props, "staffConsole.materials.confirmRemove")) return;
    await props.run(`/api/staff/clients/${props.detail.client.id}/materials/${itemId}`, { method: "DELETE" });
  }

  return (
    <article className="staff-card materials-card">
      <h3>{props.t("staffConsole.materials.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.materials.hint")}</p>
      {items.length > 0 && (
        <ul className="materials-list">
          {items.map((item) => {
            const id = String(item.id ?? "");
            const isFile = item.kind === "file";
            const href = isFile
              ? `/api/staff/clients/${props.detail.client.id}/attachments/${String(item.attachment_id ?? "")}`
              : String(item.url ?? "#");
            return (
              <li key={id} className="materials-item">
                <div className="card-row">
                  <a href={href} target="_blank" rel="noreferrer">{String(item.label ?? "")}</a>
                  <span className="materials-item-meta">
                    <span className={`note-state-chip ${item.scope === "legacy" ? "is-sent" : "is-new"}`}>
                      {props.t(item.scope === "legacy" ? "staffConsole.materials.scopeLegacy" : "staffConsole.materials.scopeKnowhow")}
                    </span>
                    <button className="text-button danger" type="button" onClick={() => void remove(id)}>
                      {props.t("staffConsole.materials.remove")}
                    </button>
                  </span>
                </div>
                {typeof item.note === "string" && item.note && <p className="materials-item-note">{String(item.note)}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {items.length === 0 && <p className="staff-card-hint">{props.t("staffConsole.materials.empty")}</p>}
      <form className="editor-form" onSubmit={addLink}>
        <div className="inline-fields">
          <label className="materials-scope">
            <input type="radio" name="material-scope" checked={scope === "knowhow"} onChange={() => setScope("knowhow")} />
            <span>{props.t("staffConsole.materials.scopeKnowhow")}</span>
          </label>
          <label className="materials-scope">
            <input type="radio" name="material-scope" checked={scope === "legacy"} onChange={() => setScope("legacy")} />
            <span>{props.t("staffConsole.materials.scopeLegacy")}</span>
          </label>
        </div>
        <input type="text" value={note} maxLength={500} placeholder={props.t("staffConsole.materials.note")}
          onChange={(event) => setNote(event.target.value)} />
        <div className="inline-fields">
          <input type="url" value={linkUrl} placeholder={props.t("staffConsole.materials.linkUrl")}
            onChange={(event) => setLinkUrl(event.target.value)} />
          <input type="text" value={linkLabel} maxLength={160} placeholder={props.t("staffConsole.materials.linkLabel")}
            onChange={(event) => setLinkLabel(event.target.value)} />
        </div>
        <div className="button-row">
          <button className="secondary-button compact-button" type="submit" disabled={state === "busy" || !linkUrl.trim()}>
            {props.t("staffConsole.materials.addLink")}
          </button>
          <button className="secondary-button compact-button" type="button" disabled={state === "busy"}
            onClick={() => fileRef.current?.click()}>
            {props.t(state === "busy" ? "staffConsole.materials.uploading" : "staffConsole.materials.upload")}
          </button>
          <input ref={fileRef} type="file" hidden accept="audio/*,image/png,image/jpeg,image/webp,application/pdf" onChange={(event) => void upload(event)} />
        </div>
        <small className="staff-card-hint">{props.t("staffConsole.materials.fileTypes")}</small>
        {state === "error" && <p className="message error-message" role="alert">{props.t("common.error")}</p>}
      </form>
    </article>
  );
}

// "Zpětné doplnění" (D18): a client who is already partway gets their done
// steps marked in ONE guarded S25 batch - checkbox prefix, one reason. The
// entry point (first unchecked step) opens in its natural state.
export const BACKFILL_STEPS: Array<{ key: SectionKey; entry: "next" | "working" }> = [
  { key: "onboarding_call", entry: "next" },
  { key: "q1", entry: "next" },
  { key: "knowhow", entry: "working" },
  { key: "training", entry: "working" },
  { key: "beta", entry: "next" },
  { key: "marketing_call", entry: "next" },
  { key: "q2", entry: "next" }
];
export const STATE_RANK: Record<string, number> = { locked: 0, next: 1, working: 2, done: 3 };

export function BackfillCard(props: StaffPaneProps) {
  // Default entry point = marketing questionnaire (q2): everything before it
  // pre-checked, the partner unchecks what the client has NOT done yet.
  const [checked, setChecked] = useState<Set<SectionKey>>(
    () => new Set(BACKFILL_STEPS.slice(0, -1).map((step) => step.key))
  );
  const [reason, setReason] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const sectionsByKey = new Map(props.detail.sections.map((section) => [section.key, section]));

  function toggle(index: number, value: boolean) {
    // Prefix semantics: the journey is linear, so checking step N checks
    // everything before it and unchecking N unchecks everything after.
    setChecked(new Set(value
      ? BACKFILL_STEPS.slice(0, index + 1).map((step) => step.key)
      : [...checked].filter((key) => BACKFILL_STEPS.findIndex((step) => step.key === key) < index)));
    setState("idle");
  }

  const entryIndex = BACKFILL_STEPS.findIndex((step) => !checked.has(step.key));
  const entry = entryIndex === -1 ? null : BACKFILL_STEPS[entryIndex];
  // Only sections that actually move FORWARD ride in the batch.
  const doneMoves = BACKFILL_STEPS.slice(0, entryIndex === -1 ? BACKFILL_STEPS.length : entryIndex)
    .filter((step) => STATE_RANK[String(sectionsByKey.get(step.key)?.state ?? "locked")] < STATE_RANK.done)
    .map((step) => step.key);
  const entrySection = entry ? sectionsByKey.get(entry.key) : undefined;
  const entryMoves = Boolean(entry && entrySection
    && STATE_RANK[String(entrySection.state)] < STATE_RANK[entry.entry]);
  const nothingToDo = doneMoves.length === 0 && !entryMoves;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!reason.trim() || nothingToDo) return;
    // The batch needs a forward-moving TARGET section; when the entry step is
    // already open, the deepest done-move becomes the target instead.
    const targetKey = entryMoves && entry ? entry.key : doneMoves[doneMoves.length - 1];
    const targetState = entryMoves && entry ? entry.entry : "done";
    const target = sectionsByKey.get(targetKey);
    if (!target) return;
    const relatedStates = Object.fromEntries(doneMoves.filter((key) => key !== targetKey).map((key) => [key, "done"]));
    const expectedVersions = Object.fromEntries(
      [targetKey, ...Object.keys(relatedStates)].map((key) => [key, Number(sectionsByKey.get(key as SectionKey)?.rowVersion ?? 0)])
    );
    setState("busy");
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/sections/${targetKey}/transition`,
      {
        method: "POST",
        body: {
          targetState,
          rowVersion: target.rowVersion,
          reason: reason.trim(),
          ...(Object.keys(relatedStates).length ? { relatedStates } : {}),
          expectedVersions
        }
      }
    );
    if (response.ok) { setState("done"); setReason(""); } else setState("error");
  }

  return (
    <article className="staff-card backfill-card">
      <h3>{props.t("staffConsole.backfill.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.backfill.hint")}</p>
      <form className="editor-form" onSubmit={submit}>
        <fieldset className="backfill-steps">
          <legend>{props.t("staffConsole.backfill.stepsLabel")}</legend>
          {BACKFILL_STEPS.map((step, index) => {
            const currentState = String(sectionsByKey.get(step.key)?.state ?? "locked");
            const alreadyDone = currentState === "done";
            return (
              <label key={step.key} className="backfill-step">
                <input
                  type="checkbox"
                  checked={alreadyDone || checked.has(step.key)}
                  disabled={alreadyDone || state === "busy"}
                  onChange={(event) => toggle(index, event.target.checked)}
                />
                <span>{props.t(`staffConsole.backfill.step.${step.key}`)}</span>
                {alreadyDone && <span className="note-state-chip is-handled">{props.t("staffConsole.state.done")}</span>}
              </label>
            );
          })}
        </fieldset>
        {entry && (
          <p className="staff-card-hint">
            {props.t("staffConsole.backfill.entry").replace("{step}", props.t(`staffConsole.backfill.step.${entry.key}`))}
          </p>
        )}
        <input type="text" value={reason} maxLength={300} placeholder={props.t("staffConsole.backfill.reason")}
          onChange={(event) => { setReason(event.target.value); setState("idle"); }} required />
        <div className="button-row">
          <button className="primary-button" type="submit" disabled={state === "busy" || nothingToDo || !reason.trim()}>
            {props.t(state === "busy" ? "staffConsole.backfill.submitting" : "staffConsole.backfill.submit")}
          </button>
          {nothingToDo && <span className="staff-card-hint">{props.t("staffConsole.backfill.nothing")}</span>}
          {state === "done" && <span className="message success-message">{props.t("staffConsole.backfill.done")}</span>}
          {state === "error" && <span className="message error-message">{props.t("common.error")}</span>}
        </div>
      </form>
    </article>
  );
}

// ResourceCards moved to src/resource-cards.tsx (testable module, night
// round N15: Czech field labels + admin-only raw fold).


// ---- Instance access for the partner (Pavel 2026-07-21) ----
// One card with everything about the created Buddy: the full Instance
// Creator detail on the hub (creation log + agentic + debug modes live
// there), the bot, the Drive folder, and the join-the-team flow.
export function InstanceCard(props: StaffPaneProps) {
  const [joining, setJoining] = useState<"idle" | "sending" | "sent" | "missing" | "error">("idle");
  const trialRow = props.detail.resources.find((row) => String(row.key) === "trial_link");
  const driveRow = props.detail.resources.find((row) => String(row.key) === "drive_folder");
  const teamRow = props.detail.resources.find((row) => String(row.key) === "instance_team_request");
  const trial = trialRow ? parseValue(trialRow) : {};
  const drive = driveRow ? parseValue(driveRow) : {};
  const team = teamRow ? parseValue(teamRow) : null;
  const bot = typeof trial.bot_username === "string" ? trial.bot_username.replace(/^@/, "") : null;
  const slug = typeof trial.instance_slug === "string" ? trial.instance_slug : null;
  // A running team-add resolves on the Mini: poll so the card flips to the
  // success state by itself (Pavel 2026-07-24: "potrebuju to poznat").
  const teamRequestedBy = team && typeof team.requested_by === "string" ? team.requested_by : null;
  const teamMine = !teamRequestedBy || !props.currentUserId || teamRequestedBy === props.currentUserId;
  const teamStatus = team && teamMine ? String(team.status ?? "") : null;
  const refresh = props.refresh;
  useEffect(() => {
    if (teamStatus !== "new") return;
    const timer = window.setInterval(() => { void refresh(); }, 10_000);
    return () => window.clearInterval(timer);
  }, [teamStatus, refresh]);
  if (!bot && !slug && !trialRow) return null;
  async function join() {
    setJoining("sending");
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/instance-team`, { method: "POST" });
    if (response.ok) setJoining("sent");
    else if (response.status === 409) setJoining("missing");
    else setJoining("error");
  }
  return (
    <article className="staff-card instance-card">
      <h3>{props.t("staffConsole.instance.title")}</h3>
      <div className="instance-links">
        {slug && (
          <a className="secondary-button compact-button" href={`https://hub.buddypro.app/instance-creator/${slug}`} target="_blank" rel="noreferrer">
            {props.t("staffConsole.instance.detail")}
          </a>
        )}
        {bot && (
          <a className="secondary-button compact-button" href={`https://t.me/${bot}`} target="_blank" rel="noreferrer">
            {props.t("staffConsole.instance.openBot")} @{bot}
          </a>
        )}
        {typeof drive.url === "string" && drive.url && (
          <a className="secondary-button compact-button" href={String(drive.url)} target="_blank" rel="noreferrer">
            {props.t("staffConsole.instance.drive")}
          </a>
        )}
        {typeof trial.url === "string" && trial.url && (
          <a className="secondary-button compact-button" href={String(trial.url)} target="_blank" rel="noreferrer">
            {props.t("staffConsole.instance.trial")}
          </a>
        )}
      </div>
      {slug && <p className="staff-card-hint">{props.t("staffConsole.instance.modesHint")}</p>}
      {bot && (
        <div className="instance-team">
          <h4>{props.t("staffConsole.instance.teamTitle")}</h4>
          {teamStatus === "done" ? (
            <>
              <p className="message success-message">{props.t("staffConsole.instance.teamDone")}</p>
              <div className="button-row">
                {bot && (
                  <a className="primary-button compact-button" href={`https://t.me/${bot}`} target="_blank" rel="noreferrer">
                    {props.t("staffConsole.instance.openBot")} @{bot}
                  </a>
                )}
                {slug && (
                  <a className="secondary-button compact-button" href={`https://hub.buddypro.app/instance-creator/${slug}`} target="_blank" rel="noreferrer">
                    {props.t("staffConsole.instance.manage")}
                  </a>
                )}
              </div>
            </>
          ) : teamStatus === "failed" ? (
            <>
              <p className="message error-message">{props.t("staffConsole.instance.teamFailed")}{team && typeof team.note === "string" && team.note ? ` (${team.note})` : ""}</p>
              {/* Telegram refuses to add anyone who has not written to the bot,
                  so the failure has to name that step (F58): an automatic
                  attempt cannot clear it, only the partner can. */}
              <p className="staff-card-hint">{props.t("staffConsole.instance.teamFailedHint")}</p>
              <div className="button-row">
                <button className="primary-button compact-button" type="button" disabled={joining === "sending"} onClick={() => void join()}>
                  {props.t("staffConsole.instance.retry")}
                </button>
              </div>
            </>
          ) : teamStatus === "new" ? (
            <p className="alex-status" role="status"><span className="loading-pulse" /> {props.t("staffConsole.instance.teamPending")}</p>
          ) : (
            <>
              {/* F58: with a Telegram ID on file the join is placed for the
                  partner (trained bot, or the moment the ID is saved), so the
                  card explains that instead of demanding a click. Without an
                  ID the button would only ever return the refusal, so the card
                  sends the partner to their profile first. */}
              <p className="staff-card-hint">
                {props.t(props.detail.viewer?.telegramReady
                  ? "staffConsole.instance.teamAutoHint"
                  : "staffConsole.instance.teamNeedIdHint")}
              </p>
              <div className="button-row">
                <button
                  className={`${props.detail.viewer?.telegramReady ? "secondary-button" : "primary-button"} compact-button`}
                  type="button"
                  disabled={joining === "sending"}
                  onClick={() => void join()}
                >
                  {props.t(joining === "sending" ? "staffConsole.instance.joining" : "staffConsole.instance.join")}
                </button>
                {joining === "sent" && <span className="message success-message">{props.t("staffConsole.instance.teamPending")}</span>}
                {joining === "missing" && <span className="message error-message">{props.t("staffConsole.instance.needTelegram")}</span>}
                {joining === "error" && <span className="message error-message">{props.t("common.error")}</span>}
              </div>
              {team && team.status === "failed" && <p className="message error-message">{props.t("staffConsole.instance.teamFailed")}</p>}
            </>
          )}
        </div>
      )}
    </article>
  );
}


/**
 * 🔴 F138 kolečko 2: a milestone with no label printed its own KEY, with the
 * underscores swapped for spaces. So the Přehled listed "instance created" and
 * "order links integrated" in English, in a column whose other line read "Bot
 * je převedený" - three milestones, two languages, one list. Two of the eight
 * keys the D1 CHECK allows had never been given a label
 * (`order_links_integrated` since F82, `salespage_published` since F137), and
 * the fallback made that invisible: it looked like a name rather than like a
 * missing one.
 *
 * A key nobody named is now NAMED as unnamed instead of printing itself, and
 * `tests/aktivita-f138.test.ts` reads the allowed keys straight out of the
 * latest migration, so tomorrow's milestone forces its own label without
 * anybody remembering (the guard `nastroje-f138` wrote for resource keys).
 */
export function MilestoneList(props: { items: Array<Record<string, unknown>>; t: (key: string) => string; locale: Locale }) {
  if (!props.items.length) return <p className="staff-empty">{props.t("staffConsole.none")}</p>;
  const safe = makeSafeT(props.t);
  return (
    <ul className="milestone-list">
      {props.items.map((row) => (
        <li key={String(row.key)}>
          <span aria-hidden="true">✓</span>
          <span className="break-text">
            {safe(`handover.proof.${String(row.key)}`, props.t("staffConsole.milestoneUnknown"))}
          </span>
          {typeof row.reachedAt === "string" && <time dateTime={String(row.reachedAt)}>{formatDate(String(row.reachedAt), props.locale)}</time>}
        </li>
      ))}
    </ul>
  );
}
